import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { CardHeader } from "../../../components/cards";
import { Button } from "../../../components/button";
import { Loading } from "../../../components/loading";
import { useWarehouse } from "../../warehouses/hooks";
import { useTransferStockUnit } from "../hooks";
import { QrCodeScanner } from "../components";

const SUCCESS_DISPLAY_MS = 1000;

type IScanResult =
  | { kind: "success"; code: string; label: string }
  | { kind: "error"; code: string; message: string };

type IWarehouseParams = {
  id: string;
};

export const TransferStock: React.FC = () => {
  const navigate = useNavigate();
  const { id: warehouseId } = useParams<IWarehouseParams>();

  const { data: warehouseData, loading: warehouseLoading } = useWarehouse(
    warehouseId ? +warehouseId : 0
  );
  const { transferFromScan } = useTransferStockUnit();

  const [transferredCount, setTransferredCount] = useState(0);
  const [result, setResult] = useState<IScanResult | null>(null);
  const [busy, setBusy] = useState(false);

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!warehouseId) {
      navigate("/warehouses");
    }
  }, [warehouseId]);

  const handleScan = async (code: string) => {
    if (busy || result || !warehouseId) return;

    setBusy(true);
    const response = await transferFromScan(code, +warehouseId);
    setBusy(false);

    if (response.success) {
      setTransferredCount((c) => c + 1);
      setResult({
        kind: "success",
        code,
        label: `${response.productName} — Lot ${response.batchNumber}${
          response.fromWarehouseName ? ` (depuis ${response.fromWarehouseName})` : ""
        }`,
      });
      successTimerRef.current = setTimeout(() => {
        setResult(null);
      }, SUCCESS_DISPLAY_MS);
    } else {
      setResult({ kind: "error", code, message: response.error ?? "Erreur inconnue" });
    }
  };

  const dismissError = () => setResult(null);

  if (!warehouseId || warehouseLoading) return <Loading />;

  const warehouse = warehouseData?.warehouse?.result;

  return (
    <>
      <Header
        title="Transférer du stock"
        subtitle={warehouse?.name}
        buttons={[
          {
            actionText: "Terminer",
            bgColor: "indigo",
            textColor: "white",
            link: `/warehouse/${warehouseId}`,
          },
        ]}
      />
      <div className="main-container">
        <div className="card mb-4 flex flex-wrap gap-6 text-sm">
          <div>
            <p className="text-gray-500">Entrepôt de destination</p>
            <p className="font-medium">{warehouse?.name}</p>
          </div>
        </div>

        <div className="card relative">
          <CardHeader
            title={`Scanner (${transferredCount} transféré${transferredCount > 1 ? "s" : ""})`}
          />
          <QrCodeScanner active={!result} onScan={handleScan} />

          {result && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded">
              {result.kind === "success" ? (
                <div className="bg-white rounded-lg px-6 py-5 text-center shadow-xl">
                  <p className="text-2xl mb-1">✅</p>
                  <p className="font-semibold text-gray-900">Lot transféré</p>
                  <p className="text-xs text-gray-500 mt-1">{result.label}</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg px-6 py-5 text-center shadow-xl max-w-xs">
                  <p className="text-2xl mb-1">⚠️</p>
                  <p className="font-semibold text-red-600">Échec du transfert</p>
                  <p className="text-sm text-gray-600 mt-1">{result.message}</p>
                  <p className="text-xs text-gray-400 break-all mt-2">{result.code}</p>
                  <Button className="mt-4" onClick={dismissError}>
                    Continuer le scan
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
